import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useNotification } from './CustomNotification';
import {
  createOne as createOffer,
  uploadGalleryImages,
  validate as validateOffer,
  type Offer,
} from '../data/offers';
import OfferForm, {
  type FormFieldValue,
  type OfferFormState,
} from './OfferForm';
import PageContainer from './PageContainer';
import {useLanguage} from "../../contexts/LanguageContext";

const INITIAL_FORM_VALUES: Partial<OfferFormState['values']> = {
  title: '',
  description: '',
  cost_description: '',
  images: [],
  provider_name: '',
  provider_phone_number: '',
  fly_tickets_cost: undefined,
  accommodation_cost: undefined,
  tours_cost: undefined,
  others_cost: undefined,
  seats_total: undefined,
};

export default function OfferCreate() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const notifications = useNotification();

  const [formState, setFormState] = React.useState<OfferFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));
  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = React.useCallback(
    (newFormValues: Partial<OfferFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<OfferFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof OfferFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<OfferFormState['values']>) => {
        const { issues } = validateOffer(values);
        const errorMessage = issues?.find((issue) => issue.path?.[0] === name)?.message;
        setFormErrors({
          ...formErrors,
          [name]: errorMessage ? t(errorMessage) : undefined,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues, t],
  );

  const handleFormReset = React.useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = React.useCallback(async () => {
    // Validate form data
    const { issues } = validateOffer(formValues);
    const errors: Partial<OfferFormState['errors']> = {};

    if (issues && issues.length > 0) {
      issues.forEach((issue) => {
        if (issue.path?.[0]) {
          errors[issue.path[0] as keyof OfferFormState['errors']] = t(issue.message);
        }
      });
    }

    // Validate images
    const images = formValues.images as File[] | undefined;
    if (!images || images.length === 0) {
      errors.images = t('validation.atLeastOneImageRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    try {
      // Step 1: Create the offer with all fields
      const offerData = {
        title: formValues.title,
        description: formValues.description,
        cost_description: formValues.cost_description,
        seats_total: formValues.seats_total,
        scope: 'ALL',
        country_id: formValues.country_id,
        provider_name: formValues.provider_name,
        provider_phone_number: formValues.provider_phone_number,
        fly_tickets_cost: formValues.fly_tickets_cost,
        accommodation_cost: formValues.accommodation_cost,
        tours_cost: formValues.tours_cost,
        others_cost: formValues.others_cost,
      };
      const createdOffer = await createOffer(offerData as Omit<Offer, 'id'>);

      // Step 2: Upload images to the gallery
      if (images && images.length > 0) {
        await uploadGalleryImages(createdOffer.id, images);
      }

      notifications.show(t('offers.createSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      navigate('/offers');
    } catch (createError) {
      notifications.show(
        `${t('offers.createFailed')} ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [formValues, navigate, notifications, setFormErrors]);

  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';

  const handleBack = React.useCallback(() => {
    navigate('/offers');
  }, [navigate]);

  return (
    <PageContainer
      breadcrumbs={[{ title: t('offers.title'), path: '/offers' }, { title: t('offers.create') }]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={{
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: '#31394a',
              color: 'white'
            },
          }}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <OfferForm
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel={t('common.create')}
      />
    </PageContainer>
  );
}

